import { Card } from "@mui/material";
import SoftBox from "components/SoftBox";
import { useEffect, useRef } from "react";
import { getUproColor } from "utils/colors";
import CrearCuenta from "../components/CrearCuentaProveedor";

function FixedShapesBackground() {
  const canvasRef = useRef(null);
  const shapesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);
    let animationFrameId;

    const defineShapes = (currentW, currentH) => [
      {
        type: "circle",
        color: "#67C1B8",
        x: currentW * 0.15,
        y: currentH * 0.2,
        size: Math.min(currentW, currentH) * 0.15,
        angle: Math.PI / 6,
        animOffset: Math.random() * 10,
        animSpeed: 0.0005,
        animMagnitude: Math.min(currentW, currentH) * 0.015,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0.8,
        y: currentH * 0.3,
        size: Math.min(currentW, currentH) * 0.22,
        angle: -Math.PI / 4,
        animOffset: Math.random() * 10,
        animSpeed: 0.0006,
        animMagnitude: Math.min(currentW, currentH) * 0.01,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0,
        y: currentH * 0.12,
        size: Math.min(currentW, currentH) * 0.22,
        angle: -Math.PI / 4,
        animOffset: Math.random() * 10,
        animSpeed: 0.0006,
        animMagnitude: Math.min(currentW, currentH) * 0.01,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0.23,
        y: currentH * 1,
        size: Math.min(currentW, currentH) * 0.22,
        angle: -Math.PI / 4,
        animOffset: Math.random() * 10,
        animSpeed: 0.0006,
        animMagnitude: Math.min(currentW, currentH) * 0.01,
      },
      {
        type: "x",
        color: "#ffffff",
        x: currentW * 0.75,
        y: currentH * 0.8,
        size: Math.min(currentW, currentH) * 0.13,
        angle: 0,
        animOffset: Math.random() * 10,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "x",
        color: "#ffffff",
        x: currentW * 0.13,
        y: currentH * 0.9,
        size: Math.min(currentW, currentH) * 0.05,
        angle: 130 * (Math.PI / 180),
        animOffset: Math.random() * 10,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "x",
        color: "#ffffff",
        x: currentW * 0.19,
        y: currentH * 0.23,
        size: Math.min(currentW, currentH) * 0.04,
        angle: 45 * (Math.PI / 180),
        animOffset: Math.random() * 10,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "circle",
        color: "#67C1B8",
        x: currentW * 1,
        y: currentH * 0.1,
        size: Math.min(currentW, currentH) * 0.16,
        angle: 0,
        animOffset: Math.random() * 10,
        animSpeed: 0.0007,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "circle",
        color: "#67C1B8",
        x: currentW * 0,
        y: currentH * 1,
        size: Math.min(currentW, currentH) * 0.35,
        angle: 0,
        animOffset: Math.random() * 20,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0.5,
        y: currentH * 0.1,
        size: Math.min(currentW, currentH) * 0.09,
        angle: Math.PI / 3,
        animOffset: Math.random() * 10,
        animSpeed: 0.0005,
        animMagnitude: Math.min(currentW, currentH) * 0.005,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 1,
        y: currentH * 1,
        size: Math.min(currentW, currentH) * 0.5,
        angle: Math.PI / 3,
        animOffset: Math.random() * 10,
        animSpeed: 0.0015,
        animMagnitude: Math.min(currentW, currentH) * 0.005,
      },
    ];

    shapesRef.current = defineShapes(w, h);

    const animate = (time) => {
      ctx.clearRect(0, 0, w, h);

      shapesRef.current.forEach((s) => {
        const currentY = s.y + Math.sin(time * s.animSpeed + s.animOffset) * s.animMagnitude;

        // Animación de shake para 'x'
        const angleShake =
          s.type === "x" ? Math.sin(time * s.animSpeed * 2 + s.animOffset) * (Math.PI / 32) : 0;

        ctx.save();
        ctx.translate(s.x, currentY);
        ctx.rotate(s.angle + angleShake);
        ctx.strokeStyle = s.color;
        ctx.fillStyle = s.color;
        ctx.lineWidth = Math.max(3, s.size / 25);
        ctx.globalAlpha = 1;

        switch (s.type) {
          case "circle":
            ctx.beginPath();
            ctx.arc(0, 0, s.size / 2, 0, 2 * Math.PI);
            ctx.fill();
            break;
          case "donut":
            const outerRadius = s.size / 2;
            const innerRadius = s.size / 4;
            ctx.beginPath();
            ctx.arc(0, 0, outerRadius, 0, 2 * Math.PI);
            ctx.fill();
            if (innerRadius > 0) {
              ctx.globalCompositeOperation = "destination-out";
              ctx.beginPath();
              ctx.arc(0, 0, innerRadius, 0, Math.PI * 2);
              ctx.fill();
              ctx.globalCompositeOperation = "source-over";
            }
            break;
          case "x":
            const maxLength = s.size / Math.SQRT2;
            const currentLength = maxLength / 2;
            ctx.beginPath();
            ctx.moveTo(-currentLength, -currentLength);
            ctx.lineTo(currentLength, currentLength);
            ctx.moveTo(currentLength, -currentLength);
            ctx.lineTo(-currentLength, currentLength);
            ctx.stroke();
            break;
        }

        ctx.restore();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    const handleResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      shapesRef.current = defineShapes(w, h);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}

function Formulario() {
  return (
    <SoftBox
      sx={{
        userSelect: "none",
        position: "relative",
        overflowX: "hidden",
        backgroundColor: getUproColor("uproPink"),
        overflowY: "auto",
        display: { xs: "block", md: "flex" },
        justifyContent: { md: "center" },
        alignItems: { md: "center" },
        height: { xs: "auto", md: "100vh" },
      }}
      pt={4}
      pb={{ xs: 10, md: 0 }}
    >
      <FixedShapesBackground />

      <SoftBox
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: { xs: "100%", sm: "70%" },
          mx: "auto",
          p: { xs: 3, md: 4 },
        }}
      >
        <Card>
          <CrearCuenta handleCancel={() => (window.location.href = "/login")} />
        </Card>
      </SoftBox>
    </SoftBox>
  );
}

export default Formulario;
